import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setDropdownOpen(false);
  };

  const parallaxOffset = scrollY * 0.35;

  return (
    <section className="relative min-h-[620px] md:min-h-[720px] flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Lifestyle%20flatlay%20with%20stylish%20sneakers%20handbag%20sunglasses%20and%20fashion%20accessories%20artfully%20arranged%20around%20open%20laptop%20on%20warm%20terracotta%20textile%20surface%20Mediterranean%20summer%20aesthetic%20golden%20afternoon%20sunlight%20casting%20long%20artistic%20shadows%20olive%20branches%20detail%20modern%20bohemian%20editorial%20photography&width=1800&height=1200&seq=hero-bg-unique-v2&orientation=landscape')`,
          transform: `translateY(${parallaxOffset}px)`,
        }}
      ></div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>

      <div className="absolute inset-0 bg-background-50/20"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
        <div className="animate-fade-in-up">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/90 text-background-50 text-xs font-semibold tracking-wider uppercase mb-6">
            {t('hero_badge')}
          </span>
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 font-heading leading-tight mb-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          {t('hero_title_1')}
          <br />
          <span className="text-accent-300">{t('hero_title_2')}</span>
        </h1>

        <p className="text-base md:text-lg text-background-50/85 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {t('hero_subtitle')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-900/20"
          >
            <i className="ri-shopping-bag-3-line"></i>
            {t('hero_btn_primary')}
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50/90 text-foreground-900 font-semibold text-base hover:bg-background-50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            <i className="ri-flashlight-line"></i>
            {t('hero_btn_secondary')}
          </a>
        </div>

        <div ref={dropdownRef} className="relative inline-block animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-3 px-6 py-3 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/25 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-list-check-3"></i>
            {t('category_select')}
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-xl shadow-xl border border-background-200/70 py-2 max-h-72 overflow-y-auto z-20 animate-fade-in">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors duration-150 flex items-center justify-between cursor-pointer group"
                >
                  <span className="whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-400 group-hover:text-accent-600 whitespace-nowrap ml-3">{cat.nameEn}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <i className="ri-arrow-down-line text-background-50/50 text-2xl"></i>
      </div>
    </section>
  );
}